import { useCallback, useState } from 'react'
import { useCaseContext } from '@broker-crm-contexts'
import { Typography, useTheme } from '@mui/material'
import { FormattedMessage } from 'react-intl'

import { MortgageReason, ReferralCaseInput, useCreateReferralCaseMutation } from '@acre/graphql'
import { Alert, DialogForm } from '@acre/design-system'

import { messagePrefix } from './ReferClientModal.helpers'
import ReferClientModalFields from './ReferClientModalFields'
import ReferClientModalFooter from './ReferClientModalFooter'

type Props = {
  isVisible: boolean
  onClose: () => void
}

type ReferClientFormValues = {
  refer_to_firm: string
  assign_to_advisor: boolean
  select_advisor: string
}

const ReferClientModal = ({ isVisible, onClose }: Props) => {
  const theme = useTheme()
  const { details: caseDetails, id: caseId } = useCaseContext()
  const [createReferralCase, { loading: loadingCreateReferralCase }] = useCreateReferralCaseMutation()
  const [successAlert, setSuccessAlert] = useState(false)

  const handleSubmit = useCallback(
    async (values: ReferClientFormValues) => {
      // only refer advisor if user has selected to
      const target_user_id = values.assign_to_advisor ? values.select_advisor : undefined

      // must refer to org or advisor not both
      const target_organisation_id = !target_user_id ? values.refer_to_firm : undefined

      const input: ReferralCaseInput = {
        case_id: caseId,
        client_ids: caseDetails?.client_ids ?? [],
        preference_mortgage_reason: MortgageReason.ReasonProtection,
        target_user_id,
        target_organisation_id,
        fee_option_title: caseDetails?.fee_option_title,
      }
      await createReferralCase({ variables: { input } })
      setSuccessAlert(true)
    },
    [caseDetails?.client_ids, caseDetails?.fee_option_title, caseId, createReferralCase, setSuccessAlert],
  )

  const Header = (
    <Typography fontSize={theme.typography.pxToRem(16)} fontWeight={700}>
      <FormattedMessage id={`${messagePrefix}.modal.heading`} />
    </Typography>
  )

  return (
    <>
      <DialogForm
        open={isVisible}
        FormProps={{
          subscription: { pristine: true, initialValues: true, values: true, valid: true, dirtyFields: true },
        }}
        onClose={onClose}
        title={Header}
        initialValues={{ assign_to_advisor: false }}
        actions={<ReferClientModalFooter onClose={onClose} />}
        onSubmit={handleSubmit}
        loading={loadingCreateReferralCase}
      >
        <ReferClientModalFields />
      </DialogForm>
      {successAlert && (
        <Alert
          id={'ClientReferralSuccessAlert'}
          label={<FormattedMessage id={`${messagePrefix}.successAlert`} />}
          isSuccess
          onClick={() => setSuccessAlert(false)}
        />
      )}
    </>
  )
}

export default ReferClientModal
